import { useEffect } from 'react';
import { useSearchParams } from '@remix-run/react';
import {
    HDE_EVENT_ID_PARAM,
    HDE_MODAL_PARAM,
    HDE_SUBMITTED_PARAM,
} from '../constants';
import type { LocationOption } from '../components/registration/types';

const MODAL_TYPE_MAP = {
    homeowner: 'Homeowner' as const,
    professional: 'Professional' as const,
    exhibitor: 'Exhibitor' as const,
} as const;

interface UseEnableFormAutoOpenOptions {
    setIsOpen: (open: boolean) => void;
    setSocialLoginSubmitted: (value: boolean) => void;
    setSocialLoginResumed?: (value: boolean) => void;
    setLocationOptions: (options: LocationOption[]) => void;
    locationOptionsFromModules: LocationOption[];
    setSocialLoginEventId?: (eventId: string | null) => void;
    setApplicationType?: (
        type: 'Homeowner' | 'Professional' | 'Exhibitor',
    ) => void;
}

export function useEnableFormAutoOpen({
    setIsOpen,
    setSocialLoginSubmitted,
    setSocialLoginResumed,
    setLocationOptions,
    locationOptionsFromModules,
    setSocialLoginEventId,
    setApplicationType,
}: UseEnableFormAutoOpenOptions): void {
    const [searchParams, setSearchParams] = useSearchParams();

    useEffect(() => {
        const modalParam = searchParams.get(HDE_MODAL_PARAM);
        if (!modalParam || !(modalParam in MODAL_TYPE_MAP)) {
            return;
        }

        const applicationType =
            MODAL_TYPE_MAP[modalParam as keyof typeof MODAL_TYPE_MAP];
        setIsOpen(true);
        // OAuth no longer auto-submits HDE registrations; ignore legacy hde-submitted URLs.
        setSocialLoginSubmitted(false);
        setSocialLoginResumed?.(applicationType !== 'Homeowner');
        setSocialLoginEventId?.(searchParams.get(HDE_EVENT_ID_PARAM));
        setApplicationType?.(applicationType);

        if (locationOptionsFromModules.length > 0) {
            setLocationOptions(locationOptionsFromModules);
        }

        const next = new URLSearchParams(searchParams);
        next.delete(HDE_MODAL_PARAM);
        next.delete(HDE_SUBMITTED_PARAM);
        next.delete(HDE_EVENT_ID_PARAM);
        setSearchParams(next, { replace: true });
    }, [
        searchParams,
        setIsOpen,
        setSearchParams,
        setSocialLoginSubmitted,
        setSocialLoginResumed,
        setLocationOptions,
        locationOptionsFromModules,
        setSocialLoginEventId,
        setApplicationType,
    ]);
}
